import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { assetUrl } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Trash2, Plus, Minus, ShoppingBag, AlertTriangle } from 'lucide-react';
import { useCurrency } from '@/context/CurrencyContext';
import { useLanguage } from '@/context/LanguageContext';
import { useSiteSettings } from '@/context/SiteSettingsContext';

const Cart = () => {
  const { cart, updateQty, remove } = useCart();
  const { format } = useCurrency();
  const { t, lang, tModel } = useLanguage();
  const { settings } = useSiteSettings();
  const navigate = useNavigate();
  const storeOpen = settings.store_open !== false;
  const closedMsg = (lang === 'ar' ? settings.store_closed_message_ar : settings.store_closed_message)
    || settings.store_closed_message
    || t('closure.cannotCheckout', 'Checkout is paused while the store is closed.');

  if (!cart.items || cart.items.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 text-center">
        <ShoppingBag className="w-10 h-10 text-white/40 mx-auto" />
        <h1 className="font-display text-3xl font-bold mt-4">{t('cart.empty', 'Your cart is empty')}</h1>
        <p className="text-white/60 mt-2">{t('cart.continueShopping', 'Start designing to add products to your cart.')}</p>
        <Link to="/shop"><Button data-testid="cart-shop-button" className="mt-6 jw-btn-glossy text-white">{t('landing.browseShop', 'Browse Shop')}</Button></Link>
      </div>
    );
  }

  const totalQty = cart.items.reduce((s, i) => s + i.quantity, 0);
  const discount = totalQty >= 50 ? cart.subtotal * 0.15 : totalQty >= 20 ? cart.subtotal * 0.10 : totalQty >= 10 ? cart.subtotal * 0.05 : 0;
  const shipping = cart.subtotal >= 35 ? 0 : 4.99;
  const total = cart.subtotal - discount + shipping;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-display text-3xl sm:text-4xl font-bold">{t('cart.title', 'Your Cart')}</h1>
      <div className="grid lg:grid-cols-[1fr_360px] gap-8 mt-6">
        <div className="space-y-3">
          {cart.items.map((it) => (
            <Card key={it.id} className="jw-card p-4 flex gap-4 items-center">
              <div className="w-20 h-20 rounded-md bg-[color:var(--jw-navy-950)] overflow-hidden shrink-0">
                <img src={it.user_design?.preview_url ? assetUrl(it.user_design.preview_url) : (it.design?.image_url ? assetUrl(it.design.image_url) : it.product?.image)} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-white truncate">{it.product?.name}</p>
                <p className="text-white/60 text-xs">{tModel(it.variant)} · {it.user_design?.name || it.design?.name || 'Custom design'}</p>
                <p className="text-white/80 text-sm mt-1">{format(it.price)}</p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="secondary" size="icon" onClick={() => updateQty(it.id, it.quantity - 1)} data-testid={`cart-decrease-${it.id}-button`} className="bg-white/[0.06] text-white border border-white/10 h-8 w-8"><Minus className="w-3 h-3" /></Button>
                <span className="w-6 text-center text-white">{it.quantity}</span>
                <Button variant="secondary" size="icon" onClick={() => updateQty(it.id, it.quantity + 1)} data-testid={`cart-increase-${it.id}-button`} className="bg-white/[0.06] text-white border border-white/10 h-8 w-8"><Plus className="w-3 h-3" /></Button>
              </div>
              <Button variant="ghost" size="icon" onClick={() => remove(it.id)} data-testid={`cart-remove-${it.id}-button`} className="text-rose-300"><Trash2 className="w-4 h-4" /></Button>
            </Card>
          ))}
        </div>

        <Card className="jw-card p-5 h-fit">
          <h3 className="font-display text-lg font-semibold">{t('cart.orderSummary', 'Order Summary')}</h3>
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-white/70">{t('cart.subtotal', 'Subtotal')}</span><span className="text-white">{format(cart.subtotal)}</span></div>
            {discount > 0 && <div className="flex justify-between text-emerald-300"><span>{t('cart.bulkDiscount', 'Bulk discount')}</span><span>- {format(discount)}</span></div>}
            <div className="flex justify-between"><span className="text-white/70">{t('cart.shipping', 'Shipping')}</span><span className="text-white">{shipping === 0 ? t('cart.free', 'Free') : format(shipping)}</span></div>
            <div className="border-t border-white/10 pt-3 flex justify-between font-semibold text-white"><span>{t('cart.total', 'Total')}</span><span>{format(total)}</span></div>
          </div>
          {!storeOpen && (
            <div data-testid="cart-closure-warning" className="mt-4 flex items-start gap-2 text-xs text-rose-200 bg-rose-500/10 border border-rose-500/30 rounded-md p-2.5">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>{closedMsg}</span>
            </div>
          )}
          <Button
            onClick={() => navigate('/checkout')}
            data-testid="cart-checkout-button"
            disabled={!storeOpen}
            className="jw-btn-glossy w-full rounded-[14px] h-12 mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {storeOpen ? t('cart.checkout', 'Checkout') : t('closure.addToCartLabel', 'Orders paused')}
          </Button>
          {totalQty < 10 && storeOpen && (
            <p className="text-white/50 text-xs mt-3">
              {t('cart.addMore', 'Add {n} more items to unlock 5% bulk discount.').replace('{n}', String(10 - totalQty))}
            </p>
          )}
        </Card>
      </div>
    </div>
  );
};

export default Cart;
